import { useMutation, useQuery } from "@tanstack/react-query"
import { supabase } from "@nasti/common/supabase"
import { queryClient } from "@nasti/common/utils"
import {
  type StorageLocation,
  type BatchStorage,
  type Batch,
  CurrentBatchStorage,
} from "@nasti/common/types"
import useUserStore from "@/store/userStore"
import { invalidateSubBatchStorage } from "@/hooks/useSubBatches"

// Extended types for storage with related data
export type StorageLocationWithBatches = StorageLocation & {
  current_batches: Array<{
    batch_id: string
    stored_at: string
    notes: string | null
    batch: Batch & {
      collection: {
        id: string
        field_name: string | null
        species: { name: string } | null
      }
    }
  }>
  batch_count: number
}

export type BatchStorageWithLocation = BatchStorage & {
  location: StorageLocation
}

// Query: Get all storage locations for the user's organisation, active first
export const useStorageLocations = () => {
  return useQuery({
    queryKey: ["storageLocations", "list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("storage_locations")
        .select(`*`)
        .order("active", { ascending: false })
        .order("name")

      if (error) throw new Error(error.message)
      return data
    },
  })
}

export const useActiveStorageLocations = () => {
  const query = useStorageLocations()

  return {
    ...query,
    data: query.data?.filter((location) => location.active),
  }
}

// Query: Get detailed view of a storage location
export const useStorageLocationDetail = (locationId: string) => {
  return useQuery({
    queryKey: ["storageLocations", "detail", locationId],
    queryFn: async () => {
      // Fetch location and current batches stored here (via view)
      const { data: location, error: locError } = await supabase
        .from("storage_locations")
        .select("*")
        .eq("id", locationId)
        .single()

      if (locError) throw new Error(locError.message)

      // Fetch current batches via the view
      const { data: currentSubBatches, error: cbError } = await supabase
        .from("current_batch_storage")
        .select("*")
        .eq("location_id", locationId)
        .overrideTypes<CurrentBatchStorage[]>()

      if (cbError) throw new Error(cbError.message)

      // Fetch batch details for current batches
      const subBatchIds = [
        ...new Set((currentSubBatches ?? []).map((cb) => cb.sub_batch_id)),
      ]
      const { data: subBatches } = subBatchIds.length
        ? await supabase
            .from("sub_batches")
            .select(
              `
              *,batch:batches(*)
            `,
            )
            .in("id", subBatchIds)
        : { data: [] }

      const subBatchMap = new Map((subBatches ?? []).map((b) => [b.id, b]))

      // Fetch storage history
      const { data: storageHistory, error: shError } = await supabase
        .from("batch_storage")
        .select(
          `
          *,
          location:storage_locations!batch_storage_location_id_fkey(
            id, name, description
          ),
          sub_batch:sub_batches(batch_id)
        `,
        )
        .eq("location_id", locationId)
        .order("stored_at", { ascending: false })

      if (shError) throw new Error(shError.message)

      return {
        ...location,
        current_sub_batches: (currentSubBatches ?? []).map((cb) => ({
          ...cb,
          subBatch: subBatchMap.get(cb.sub_batch_id) ?? null,
        })),
        storage_history: storageHistory ?? [],
      }
    },
  })
}

// Query: Get current storage location for a batch (via current_batch_storage view)
export const useCurrentBatchStorage = (batchId: string) => {
  return useQuery({
    queryKey: ["subBatches", "currentStorage", batchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("current_batch_storage")
        .select(
          `
          *,
          location:storage_locations(id, name, description)
          `,
        )
        .eq("batch_id", batchId)
        .limit(1)
        .maybeSingle()

      if (error) {
        // Batch might not have a storage record
        if (error.code === "PGRST116") return null
        throw new Error(error.message)
      }
      return data
    },
  })
}

// Query: Get complete storage history for a batch (via sub_batches)
export const useCompleteBatchStorageHistory = (batchId: string) => {
  return useQuery({
    queryKey: ["subBatches", "storageHistory", batchId],
    queryFn: async () => {
      // Get all sub-batch IDs for this batch
      const { data: subBatches, error: sbError } = await supabase
        .from("sub_batches")
        .select("id")
        .eq("batch_id", batchId)

      if (sbError) throw new Error(sbError.message)
      if (!subBatches?.length) return []

      const subBatchIds = subBatches.map((sb) => sb.id)

      const { data, error } = await supabase
        .from("batch_storage")
        .select(
          `
          *,
          location:storage_locations!batch_storage_location_id_fkey(
            id,
            name,
            description
          )
        `,
        )
        .in("sub_batch_id", subBatchIds)
        .order("stored_at", { ascending: false })

      if (error) throw new Error(error.message)
      return data as BatchStorageWithLocation[]
    },
  })
}

// Mutation: Create new storage location
type CreateStorageLocationParams = {
  name: string
  description?: string
}

export const useCreateStorageLocation = () => {
  const { organisation } = useUserStore()

  return useMutation<StorageLocation, Error, CreateStorageLocationParams>({
    mutationFn: async ({ name, description }) => {
      if (!organisation?.id) throw new Error("No organisation found")

      const { data, error } = await supabase
        .from("storage_locations")
        .insert({
          name,
          description,
          organisation_id: organisation.id,
        })
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as StorageLocation
    },
    onSuccess: (newLocation) => {
      // Invalidate storage locations list
      queryClient.invalidateQueries({
        queryKey: ["storageLocations"],
      })

      // Set initial data for the new location detail
      queryClient.setQueryData(["storageLocations", "detail", newLocation.id], {
        ...newLocation,
        current_batches: [],
        storage_history: [],
      })
    },
  })
}

// Mutation: Update storage location
type UpdateStorageLocationParams = {
  id: string
  name?: string
  description?: string
  active?: boolean
}

export const useUpdateStorageLocation = () => {
  return useMutation<StorageLocation, Error, UpdateStorageLocationParams>({
    mutationFn: async ({ id, name, description, active }) => {
      const updates: Omit<UpdateStorageLocationParams, "id"> = {}
      if (name !== undefined) updates.name = name
      if (description !== undefined) updates.description = description
      if (active !== undefined) updates.active = active

      const { data, error } = await supabase
        .from("storage_locations")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: (updatedLocation) => {
      // Invalidate and update caches
      queryClient.invalidateQueries({
        queryKey: ["storageLocations"],
      })

      // Update detail cache
      queryClient.setQueryData<StorageLocation>(
        ["storageLocations", "detail", updatedLocation.id],
        (oldData) => ({
          ...oldData,
          ...updatedLocation,
        }),
      )
    },
  })
}

type RemoveStorageLocationResult = {
  locationId: string
  action: "deleted" | "retired"
}

// Mutation: Delete a never-used location, or retire one with storage history
export const useDeleteStorageLocation = () => {
  return useMutation<RemoveStorageLocationResult, Error, string>({
    mutationFn: async (locationId) => {
      const { data, error } = await supabase.rpc("fn_remove_storage_location", {
        p_location_id: locationId,
      })

      if (error) throw new Error(error.message)
      if (data !== "deleted" && data !== "retired") {
        throw new Error("Unexpected storage location removal result")
      }

      return { locationId, action: data }
    },
    onSuccess: ({ locationId }) => {
      queryClient.invalidateQueries({
        queryKey: ["storageLocations"],
      })

      queryClient.removeQueries({
        queryKey: ["storageLocations", "detail", locationId],
      })
    },
  })
}

// Mutation: Move a sub-batch to a storage location
type MoveBatchToStorageParams = {
  batchId: string
  subBatchId: string
  locationId: string | null
  notes?: string
  effectiveAt: string
}

export const useMoveBatchToStorage = () => {
  return useMutation<BatchStorage, Error, MoveBatchToStorageParams>({
    mutationFn: async ({ subBatchId, locationId, notes, effectiveAt }) => {
      const { data, error } = await supabase.rpc("fn_set_sub_batch_storage", {
        p_sub_batch_id: subBatchId,
        ...(locationId && { p_location_id: locationId }),
        p_effective_at: effectiveAt,
        p_notes: notes,
      })

      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: (_, variables) => {
      invalidateSubBatchStorage(variables.batchId)
    },
  })
}

// Mutation: Create storage record
type CreateStorageRecordParams = {
  locationId: string
  subBatchId: string
  batchId: string // used for cache invalidation only
  notes?: string
}

export const useCreateStorageRecord = () => {
  return useMutation<BatchStorage, Error, CreateStorageRecordParams>({
    mutationFn: async ({ locationId, subBatchId, batchId, notes }) => {
      const { data, error } = await supabase
        .from("batch_storage")
        .insert({
          notes,
          batch_id: batchId,
          location_id: locationId,
          sub_batch_id: subBatchId,
        })
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as BatchStorage
    },
    onSuccess: (_, variables) => {
      invalidateSubBatchStorage(variables.batchId)
    },
  })
}
// Mutation: Update storage record notes
type UpdateStorageRecordParams = {
  storageId: string
  locationId?: string
  notes?: string
}

export const useUpdateStorageRecord = () => {
  return useMutation<BatchStorage, Error, UpdateStorageRecordParams>({
    mutationFn: async ({ storageId, locationId, notes }) => {
      const { data, error } = await supabase
        .from("batch_storage")
        .update({ notes, location_id: locationId })
        .eq("id", storageId)
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as BatchStorage
    },
    onSuccess: (updatedStorage) => {
      invalidateSubBatchStorage(updatedStorage.batch_id)
    },
  })
}

// Query: Get batches without current storage location
export const useBatchesWithoutStorage = () => {
  return useQuery({
    queryKey: ["batches", "withoutStorage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("batches")
        .select(
          `
          *,
          collection:collection!batches_collection_id_fkey(
            id,
            field_name,
            species:species(name)
          )
        `,
        )
        .not(
          "id",
          "in",
          `(
          SELECT batch_id 
          FROM current_batch_storage 
          WHERE moved_out_at IS NULL
        )`,
        )
        .order("created_at", { ascending: false })

      if (error) throw new Error(error.message)
      return data
    },
  })
}

// Utility function to get storage summary statistics
export const useStorageSummary = () => {
  return useQuery({
    queryKey: ["storage", "summary"],
    queryFn: async () => {
      const [locationsResult, batchesResult, unstored] = await Promise.all([
        // Count total storage locations
        supabase.from("storage_locations").select("id", { count: "exact" }),

        // Count total stored batches
        supabase
          .from("current_batch_storage")
          .select("batch_id", { count: "exact" }),

        // Count batches without storage
        supabase
          .from("batches")
          .select("id")
          .not(
            "id",
            "in",
            `(
            SELECT batch_id 
            FROM current_batch_storage
          )`,
          ),
      ])

      if (locationsResult.error) throw new Error(locationsResult.error.message)
      if (batchesResult.error) throw new Error(batchesResult.error.message)
      if (unstored.error) throw new Error(unstored.error.message)

      return {
        totalLocations: locationsResult.count || 0,
        totalStoredBatches: batchesResult.count || 0,
        unstoredBatches: unstored.data?.length || 0,
        totalBatches: (batchesResult.count || 0) + (unstored.data?.length || 0),
      }
    },
  })
}
